import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

// Async thunks
export const fetchAuctions = createAsyncThunk(
  'auctions/fetchAuctions',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await axios.get('/api/auctions', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch auctions');
    }
  }
);

export const fetchAuctionById = createAsyncThunk(
  'auctions/fetchAuctionById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/api/auctions/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Auction not found');
    }
  }
);

export const placeBid = createAsyncThunk(
  'auctions/placeBid',
  async ({ auctionId, bidAmount, maxAutoBid }, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const response = await axios.post(`/api/auctions/${auctionId}/bids`,
        { user_id: auth.user.id, bid_amount: bidAmount, max_auto_bid: maxAutoBid },
        { headers: { Authorization: `Bearer ${auth.token}` } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to place bid');
    }
  }
);

export const fetchMyBids = createAsyncThunk(
  'auctions/fetchMyBids',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const response = await axios.get('/api/auctions/my-bids', {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch bids');
    }
  }
);

const initialState = {
  auctions: [],
  currentAuction: null,
  myBids: [],
  filters: {
    status: 'active',
    category: null,
    sort: 'ending_soon',
  },
  loading: false,
  bidding: false,
  error: null,
  bidError: null,
};

const auctionsSlice = createSlice({
  name: 'auctions',
  initialState,
  reducers: {
    setAuctionFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearBidError: (state) => {
      state.bidError = null;
    },
    updateAuctionBid: (state, action) => {
      // For real-time bid updates via WebSocket
      const { auctionId, currentBid, bidCount } = action.payload;
      const auction = state.auctions.find(a => a.id === auctionId);
      if (auction) {
        auction.current_bid = currentBid;
        auction.bid_count = bidCount;
      }
      if (state.currentAuction?.id === auctionId) {
        state.currentAuction.current_bid = currentBid;
        state.currentAuction.bid_count = bidCount;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch auctions
      .addCase(fetchAuctions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAuctions.fulfilled, (state, action) => {
        state.loading = false;
        state.auctions = action.payload;
      })
      .addCase(fetchAuctions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch auction by ID
      .addCase(fetchAuctionById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAuctionById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentAuction = action.payload;
      })
      .addCase(fetchAuctionById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Place bid
      .addCase(placeBid.pending, (state) => {
        state.bidding = true;
        state.bidError = null;
      })
      .addCase(placeBid.fulfilled, (state, action) => {
        state.bidding = false;
        if (state.currentAuction) {
          state.currentAuction.current_bid = action.payload.bid_amount;
          state.currentAuction.bid_count = (state.currentAuction.bid_count || 0) + 1;
        }
      })
      .addCase(placeBid.rejected, (state, action) => {
        state.bidding = false;
        state.bidError = action.payload;
      })
      // Fetch my bids
      .addCase(fetchMyBids.fulfilled, (state, action) => {
        state.myBids = action.payload;
      });
  },
});

export const { setAuctionFilters, clearBidError, updateAuctionBid } = auctionsSlice.actions;
export default auctionsSlice.reducer;
